import React from 'react'
import { Card, CardContent } from '@/shared/ui/Card'
import { Button, Radio, Input, Label } from '@/shared/ui'
import type { BillingManageMode, StripePortalConfig } from '../services/settingsService'
import { getBillingManageMode, setBillingManageMode, getStripePortalConfig, setStripePortalConfig } from '../services/settingsService'

export default function StripeBillingSettings() {
  const [mode, setMode] = React.useState<BillingManageMode>('portal')
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [portalConfigIdTest, setPortalConfigIdTest] = React.useState<string>('')
  const [portalReturnUrlTest, setPortalReturnUrlTest] = React.useState<string>('')
  const [portalConfigIdLive, setPortalConfigIdLive] = React.useState<string>('')
  const [portalReturnUrlLive, setPortalReturnUrlLive] = React.useState<string>('')

  React.useEffect(() => {
    (async () => {
      try {
        setError(null)
        setMode(await getBillingManageMode())
        const cfg = await getStripePortalConfig().catch(() => ({ test:{configuration_id:null, return_url:null}, live:{configuration_id:null, return_url:null} } as StripePortalConfig))
        setPortalConfigIdTest(cfg.test.configuration_id || '')
        setPortalReturnUrlTest(cfg.test.return_url || '')
        setPortalConfigIdLive(cfg.live.configuration_id || '')
        setPortalReturnUrlLive(cfg.live.return_url || '')
      } catch (e: any) {
        setError(e?.message || 'Falha ao carregar configuração')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  function isValidUrl(u: string): boolean {
    if (!u) return true
    try { const x = new URL(u); return x.protocol === 'http:' || x.protocol === 'https:' } catch { return false }
  }
  function isValidBpc(id: string): boolean { return !id || /^bpc_\w+$/i.test(id) }

  async function onSave() {
    try {
      setSaving(true)
      setError(null)
      if (!isValidBpc(portalConfigIdTest) || !isValidBpc(portalConfigIdLive)) throw new Error('configuration_id inválido (esperado iniciar com bpc_)')
      if (!isValidUrl(portalReturnUrlTest) || !isValidUrl(portalReturnUrlLive)) throw new Error('return_url inválido (use http(s)://...)')
      await setBillingManageMode(mode)
      await setStripePortalConfig({
        test: { configuration_id: portalConfigIdTest.trim() || null, return_url: portalReturnUrlTest.trim() || null },
        live: { configuration_id: portalConfigIdLive.trim() || null, return_url: portalReturnUrlLive.trim() || null },
      })
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardContent>
        <div className="mb-3 text-sm font-medium">Gerenciamento de Cartão (Stripe)</div>
        {error && (
          <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}
        <div className="space-y-2">
          <label className={`flex items-center justify-between rounded-md border p-3 ${mode === 'portal' ? 'border-primary' : 'border-border'}`}>
            <div>
              <div className="text-sm font-medium">Portal do Stripe</div>
              <div className="text-xs text-foreground/70">Abre a página segura do Stripe para adicionar/atualizar o cartão (recomendado).</div>
            </div>
            <Radio name="billing_mode" checked={mode === 'portal'} onChange={() => setMode('portal')} />
          </label>
          <label className={`flex items-center justify-between rounded-md border p-3 ${mode === 'inline' ? 'border-primary' : 'border-border'}`}>
            <div>
              <div className="text-sm font-medium">In-app (Stripe Elements)</div>
              <div className="text-xs text-foreground/70">Coleta o cartão na nossa página usando Elements; nada é salvo no banco.</div>
            </div>
            <Radio name="billing_mode" checked={mode === 'inline'} onChange={() => setMode('inline')} />
          </label>
        </div>

        <div className="mt-4 grid gap-5">
          <div>
            <div className="mb-2 text-sm font-medium">Modo Teste</div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>configuration_id (bpc_...)</Label>
                <Input value={portalConfigIdTest} onChange={(e)=> setPortalConfigIdTest(e.target.value)} placeholder="bpc_..." />
              </div>
              <div className="space-y-1.5">
                <Label>Return URL</Label>
                <Input value={portalReturnUrlTest} onChange={(e)=> setPortalReturnUrlTest(e.target.value)} placeholder="http://localhost:3000/assinatura" />
              </div>
            </div>
          </div>
          <div>
            <div className="mb-2 text-sm font-medium">Modo Produção</div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>configuration_id (bpc_...)</Label>
                <Input value={portalConfigIdLive} onChange={(e)=> setPortalConfigIdLive(e.target.value)} placeholder="bpc_..." />
              </div>
              <div className="space-y-1.5">
                <Label>Return URL</Label>
                <Input value={portalReturnUrlLive} onChange={(e)=> setPortalReturnUrlLive(e.target.value)} placeholder="https://app.seu-dominio/assinatura" />
              </div>
            </div>
            <div className="mt-1 text-[11px] text-foreground/70">Se um campo ficar vazio, a função usa os valores genéricos (sem .test/.live) ou o SITE_URL.</div>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button onClick={onSave} disabled={loading || saving}>{saving ? 'Salvando...' : 'Salvar'}</Button>
        </div>
      </CardContent>
    </Card>
  )
}